import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  SpaceBetween,
  Table,
  Header,
  Badge,
  Modal,
  FormField,
  Input,
  Select,
  StatusIndicator,
  Container
} from '@amzn/awsui-components-react';
import SchedulerSetupModal from './SchedulerSetupModal';

// Mock data generator
const generateMockData = () => {
  return [
    {
      schedulerId: 'SCH001',
      schedulerName: 'Hourly refresh - W1 - WD5',
      datasetUrl: 'https://us-east-1.quicksight.aws',
      lastRefresh: new Date('2025-08-04T01:23:00Z').toISOString(),
      nextRefresh: new Date('2025-09-06T01:23:00Z').toISOString(),
      status: 'Active',
      associatedApps: 'ATEAM Dashboard WXYZ'
    },
    {
      schedulerId: 'SCH002',
      schedulerName: 'Refresh of dataset - QEC',
      datasetUrl: 'https://us-east-1.quicksight.aws',
      lastRefresh: new Date('2025-08-04T01:23:00Z').toISOString(),
      nextRefresh: new Date('2025-09-06T01:23:00Z').toISOString(),
      status: 'Disabled',
      associatedApps: 'ATEAM Dashboard WXYZ'
    },
    {
      schedulerId: 'SCH003',
      schedulerName: 'Cron scheduler',
      datasetUrl: 'https://us-east-1.quicksight.aws',
      lastRefresh: new Date('2025-08-04T01:23:00Z').toISOString(),
      nextRefresh: new Date('2025-09-06T01:23:00Z').toISOString(),
      status: 'Active',
      associatedApps: 'ATEAM Dashboard WXYZ\nPost-audit Reserve App'
    }
  ];
};

const DatasetRefreshSchedule = () => {
  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add', 'edit', 'view'

  // Mock dataset options for the dropdown
  const datasetOptions = [
    { label: 'https://us-east-1.quicksight.aws', value: 'https://us-east-1.quicksight.aws' },
    { label: 'https://us-west-2.quicksight.aws', value: 'https://us-west-2.quicksight.aws' },
    { label: 'https://eu-west-1.quicksight.aws', value: 'https://eu-west-1.quicksight.aws' }
  ];

  // Mock application options for multiselect
  const applicationOptions = [
    { label: 'ATEAM Dashboard WXYZ', value: 'app1' },
    { label: 'Economic Impact Dashboard', value: 'app2' },
    { label: 'Post-audit Reserve App', value: 'app3' }
  ];

  // Load initial data
  useEffect(() => {
    loadData();
  }, []);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      loadData();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, []);

  const loadData = () => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      const mockData = generateMockData();
      setDatasets(mockData);
      setLoading(false);
    }, 500);
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    });
  };

  const getStatusIndicator = (status) => {
    if (status === 'Active') {
      return (
        <SpaceBetween direction="horizontal" size="xs">
          <Box fontSize="display-l" color="text-status-success">●</Box>
          <Box>{status}</Box>
        </SpaceBetween>
      );
    } else if (status === 'Disabled') {
      return (
        <SpaceBetween direction="horizontal" size="xs">
          <Box fontSize="display-l" color="text-status-inactive">●</Box>
          <Box>{status}</Box>
        </SpaceBetween>
      );
    }
    return <Box>{status}</Box>;
  };

  const handleView = (dataset) => {
    setSelectedDataset(dataset);
    setModalMode('view');
    setShowViewModal(true);
  };

  const handleEdit = (dataset) => {
    setSelectedDataset(dataset);
    setModalMode('edit');
    setShowEditModal(true);
  };

  const handleSaveSchedule = (formData) => {
    console.log('Saving schedule:', formData);
    
    if (modalMode === 'edit' && selectedDataset) {
      // Update existing schedule
      const updatedDatasets = datasets.map(ds => 
        ds.schedulerId === selectedDataset.schedulerId 
          ? { 
              ...ds, 
              schedulerName: formData.schedulerName,
              datasetUrl: formData.datasetUrl,
              associatedApps: formData.applications.map(app => app.label).join('\n')
            }
          : ds
      );
      setDatasets(updatedDatasets);
      setShowEditModal(false);
    } else {
      // Add new schedule
      const newSchedule = {
        schedulerId: `SCH${String(datasets.length + 1).padStart(3, '0')}`,
        schedulerName: formData.schedulerName,
        datasetUrl: formData.datasetUrl,
        lastRefresh: new Date().toISOString(),
        nextRefresh: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        status: 'Active',
        associatedApps: formData.applications.map(app => app.label).join('\n')
      };
      setDatasets([...datasets, newSchedule]);
      setShowAddModal(false);
    }
    
    setSelectedDataset(null);
  };

  const handleDeleteConfirm = (dataset) => {
    setSelectedDataset(dataset);
    setShowDeleteConfirm(true);
  };

  const handleDelete = () => {
    if (selectedDataset) {
      const updatedDatasets = datasets.filter(ds => ds.schedulerId !== selectedDataset.schedulerId);
      setDatasets(updatedDatasets);
      setShowDeleteConfirm(false);
      setSelectedDataset(null);
    }
  };

  const handleAddSchedule = () => {
    setSelectedDataset(null);
    setModalMode('add');
    setShowAddModal(true);
  };

  const columnDefinitions = [
    {
      id: 'schedulerName',
      header: 'Scheduler name',
      cell: item => item.schedulerName,
      sortingField: 'schedulerName',
      width: 200
    },
    {
      id: 'datasetUrl',
      header: 'Dataset URL',
      cell: item => item.datasetUrl,
      sortingField: 'datasetUrl',
      width: 220
    },
    {
      id: 'lastRefresh',
      header: 'Last Refresh',
      cell: item => formatDateTime(item.lastRefresh),
      sortingField: 'lastRefresh',
      width: 180
    },
    {
      id: 'nextRefresh',
      header: 'Next Refresh',
      cell: item => formatDateTime(item.nextRefresh),
      sortingField: 'nextRefresh',
      width: 180
    },
    {
      id: 'status',
      header: 'Status',
      cell: item => getStatusIndicator(item.status),
      sortingField: 'status',
      width: 120
    },
    {
      id: 'associatedApps',
      header: 'Associated Apps',
      cell: item => (
        <Box>
          {item.associatedApps.split('\n').map((app, index) => (
            <div key={index}>{app}</div>
          ))}
        </Box>
      ),
      width: 200
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: item => (
        <SpaceBetween direction="horizontal" size="xs">
          <Button
            variant="normal"
            onClick={() => handleView(item)}
          >
            View
          </Button>
          <Button
            variant="icon"
            iconName="edit"
            onClick={() => handleEdit(item)}
          />
          <Button
            variant="icon"
            iconName="remove"
            onClick={() => handleDeleteConfirm(item)}
          />
        </SpaceBetween>
      ),
      width: 180
    }
  ];

  return (
    <Box padding={{ vertical: 'l', horizontal: 'l' }}>
      <SpaceBetween size="l">
        <Table
          columnDefinitions={columnDefinitions}
          items={datasets}
          loading={loading}
          loadingText="Loading schedules..."
          empty={
            <Box textAlign="center" color="inherit">
              <b>No scheduled refreshes</b>
              <Box padding={{ bottom: 's' }} variant="p" color="inherit">
                No dataset refresh schedules found.
              </Box>
            </Box>
          }
          header={
            <Header
              actions={
                <Button variant="primary" onClick={handleAddSchedule}>
                  Add schedule
                </Button>
              }
            >
              Dataset Refresh Schedules
            </Header>
          }
        />
      </SpaceBetween>

      {/* Scheduler Setup Modal - for View, Edit, and Add */}
      <SchedulerSetupModal
        visible={showViewModal || showEditModal || showAddModal}
        onDismiss={() => {
          setShowViewModal(false);
          setShowEditModal(false);
          setShowAddModal(false);
          setSelectedDataset(null);
        }}
        onSave={handleSaveSchedule}
        initialData={selectedDataset}
        datasets={datasetOptions}
        applications={applicationOptions}
        mode={modalMode}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        visible={showDeleteConfirm}
        onDismiss={() => setShowDeleteConfirm(false)}
        header="Confirm Delete"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleDelete}>
                Delete
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <Box>
          Are you sure you want to delete the schedule{' '}
          <strong>{selectedDataset?.schedulerName}</strong>?
        </Box>
      </Modal>
    </Box>
  );
};

export default DatasetRefreshSchedule;
